import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { withCommon, LLPreloader } from '../../../../components';
import {
    EasyMembershipPrompt,
    EasyMembershipBlocked,
} from '../../../../components/easy-membership';
import PaymentItem from './components/payment-item';
import config from '../../../../static/themes/config';
import { resolveLocalizedString } from '../../../../common/utils';
import { DrawerCloseButton } from '../../components';

/**
 * @file Represents React Component to render container with payment methods
 * @memberOf MyAccountPage
 *
 * @param {Object} payments - store data of payment item
 * @param {array} [user.deposits.payments] - store data for deposits
 * @param {array} [user.withdraws.payments] - store data for withdraws
 * @param {function} [user.deposits.getPayments] - function to get request data for deposits
 * @param {boolean} [user.deposits.isTableLoading] - show loading if we are waiting for deposits request
 * @param {boolean} mobileUserAgent - flag is mobile layout
 * @param {('mobile'|'tablet'|'desktop')} viewSize - modifier for css classes
 *
 * @author A.Huskova
 */

const Payments = withCommon({
    store: ({ store }) => ({
        user: store.user,
        language: store.user.language,
        isTableLoadingDeposits: store.user.deposit.isTableLoading,
        isTableLoadingWithdraws: store.user.withdraws.isTableLoading,
        goBack: store.router.goBack,
        pathname: store.router.location.pathname,
        viewSize: store.site.status.viewSize,
        membershipComplete: store.user.membershipComplete,
        identityVerified: store.user.identityVerified,
    }),
    propTypes: {
        goBack: PropTypes.func.isRequired,
        type: PropTypes.oneOf(['deposit', 'withdraws']),
        user: PropTypes.shape({
            deposits: PropTypes.shape({
                payments: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
                getPayments: PropTypes.func.isRequired,
            }),
            withdraws: PropTypes.shape({
                payments: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
                getPayments: PropTypes.func.isRequired,
            }),
        }),
    },
    defaultName: 'Payments',
    isTrans: true,
    isObserver: true,
})(({ viewSize, user, trans, type, pathname, language, membershipComplete, identityVerified }) => {
    const paymentConfig = config.payments[type];
    const noDataMessage = resolveLocalizedString(paymentConfig.noDataMessage, language);
    const action = type === 'deposit' ? 'deposit' : 'withdraw';
    const gateKind = user.membershipGateKind(action);

    useEffect(() => {
        if (gateKind) {
            user[type].clearPayments();
            return;
        }
        user[type].getPayments(paymentConfig.getDataFunctionName).then();
    }, [type, gateKind, membershipComplete, identityVerified]);

    return (
        <div className="payments">
            {(viewSize === 'desktop' && pathname !== '/account/deposit') && (
                <p className="my-account__title">
                    <span className="my-account__title-text">
                        {trans(paymentConfig.name)}
                    </span>
                    <DrawerCloseButton />
                </p>
            )}
            {gateKind === 'prompt' ? (
                <div className="payments__membership-gate">
                    <EasyMembershipPrompt />
                </div>
            ) : gateKind === 'blocked' ? (
                <div className="payments__membership-gate">
                    <EasyMembershipBlocked />
                </div>
            ) : user[type].isTableLoading ? (
                    <div className="my-account__loader-box">
                        <LLPreloader />
                    </div>
                ) : (
                    <div
                        className={`my-account__content payments__content--${
                            viewSize === 'desktop' ? 'desktop' : 'mobile'
                        }`}
                    >
                        {user[type].payments.length ? (
                            user[type].payments.map((item) => (
                                <PaymentItem key={item.id} item={item} type={type} />
                            ))
                        ) : (
                            <p className="payments__no-data">
                                {noDataMessage}
                            </p>
                        )}
                    </div>
                )
            }
        </div>
    );
});

export default Payments;
